import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  CreditCardIcon,
  BanknotesIcon,
  UserCircleIcon,
  SparklesIcon,
  TrophyIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  GiftIcon,
  PlusIcon,
  EyeIcon,
  CalendarIcon,
  UserIcon,
  PaperClipIcon,
  DocumentIcon,
  PhotoIcon,
  BriefcaseIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import api from '../services/api';
import useAuthStore from '../store/authStore';
import useStatusBarHeight from '../hooks/useStatusBarHeight';

const VipDashboard = () => {
  const location = useLocation();
  const { user } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const statusBarHeight = useStatusBarHeight();
  
  // ESC 키로 메뉴 닫기
  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };
    
    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleEsc);
      // 스크롤 방지
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const { data: vipInfo } = useQuery(
    'vip-info',
    () => api.get('/vip/info').then(res => res.data),
    {
      refetchInterval: 30000 // 30초마다 새로고침
    }
  );

  const navigation = [
    { 
      name: '대시보드', 
      href: '/vip', 
      icon: ChartBarIcon,
      current: location.pathname === '/vip' 
    },
    { 
      name: '리워드 내역', 
      href: '/vip/rewards', 
      icon: GiftIcon,
      current: location.pathname === '/vip/rewards' 
    },
    { 
      name: '인출 관리', 
      href: '/vip/withdrawals', 
      icon: BanknotesIcon,
      current: location.pathname === '/vip/withdrawals' 
    },
    { 
      name: '계좌 정보', 
      href: '/vip/account', 
      icon: CreditCardIcon,
      current: location.pathname === '/vip/account' 
    },
    { 
      name: '공지사항', 
      href: '/vip/notices', 
      icon: SparklesIcon,
      current: location.pathname === '/vip/notices' 
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* VIP 헤더 - 모바일 */}
      <div className="lg:hidden bg-gradient-to-r from-purple-600 to-pink-600 text-white sticky z-30" style={{ top: `${statusBarHeight}px` }}>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <StarIcon className="h-6 w-6 text-yellow-300" />
            <span className="font-bold text-lg">VIP 대시보드</span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        </div>
      </div>
      
      {/* 모바일 슬라이드 메뉴 */}
      <div className="lg:hidden">
        {/* 오버레이 배경 */}
        <div 
          className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${
            mobileMenuOpen 
              ? 'opacity-50' 
              : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setMobileMenuOpen(false)}
        />
        
        {/* 좌측 슬라이드 메뉴 */}
        <div className={`fixed left-0 top-0 h-full w-72 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          {/* 메뉴 헤더 */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <StarIcon className="h-6 w-6 text-yellow-300" />
                <h2 className="text-lg font-bold">VIP 메뉴</h2>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            {/* 사용자 정보 */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-full">
                <UserCircleIcon className="h-10 w-10" />
              </div>
              <div>
                <p className="font-medium">{user?.name}</p>
                <p className="text-sm opacity-90">VIP 고객</p>
              </div>
            </div>
          </div>
          
          {/* 메뉴 항목들 */}
          <nav className="p-4 space-y-2">
            {navigation.map((item, index) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${
                    item.current
                      ? 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border-l-4 border-purple-600'
                      : 'text-gray-700 hover:bg-purple-50'
                  } group flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 transform hover:scale-105`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <Icon className={`h-5 w-5 ${
                    item.current ? 'text-purple-600' : 'text-gray-500'
                  }`} />
                  <span>{item.name}</span>
                  {item.current && (
                    <ChevronRightIcon className="h-4 w-4 ml-auto text-purple-600" />
                  )}
                </Link>
              );
            })}
          </nav>
          
          {/* 하단 정보 */}
          <div className="absolute bottom-20 left-0 right-0 p-4 border-t border-gray-200">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3">
              <p className="text-xs text-purple-600 mb-1">현재 잔액</p>
              <p className="text-lg font-bold text-purple-900">
                {formatCurrency(vipInfo?.vipInfo?.current_balance)}
              </p>
            </div>
            <Link
              to="/projects"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-3 w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 rounded-lg text-center font-medium hover:shadow-lg transition-all block"
            >
              프로젝트 보러가기
            </Link>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* 데스크톱 사이드바 */}
        <div className="hidden lg:flex flex-col w-64 bg-white/80 backdrop-blur-sm shadow-lg max-h-screen sticky top-0">
          {/* VIP 프로필 */}
          <div className="flex-shrink-0 p-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-white/20 rounded-full">
                <UserCircleIcon className="h-8 w-8" />
              </div>
              <div>
                <p className="text-sm opacity-90">VIP 고객</p>
                <p className="font-bold text-lg">{user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StarIcon className="h-5 w-5 text-yellow-300" />
              <span className="text-sm">리워드 {vipInfo?.vipInfo?.reward_rate || 3}% 적립</span>
            </div>
          </div>

          <nav className="flex-1 mt-6 px-4 overflow-y-auto min-h-0">
            <div className="space-y-2 pb-4">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      item.current
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg scale-105'
                        : 'text-gray-700 hover:bg-purple-50 hover:scale-102'
                    } group flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200`}
                  >
                    <Icon className="h-5 w-5" />
                    {item.name}
                    {item.current && (
                      <ChevronRightIcon className="h-4 w-4 ml-auto" />
                    )}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* 빠른 통계 */}
          <div className="flex-shrink-0 p-4 bg-gradient-to-t from-white to-transparent border-t border-gray-200">
            <div className="bg-purple-50 rounded-xl p-4 mb-3">
              <p className="text-xs text-purple-600 mb-1">현재 잔액</p>
              <p className="text-xl font-bold text-purple-900">
                {formatCurrency(vipInfo?.vipInfo?.current_balance)}
              </p>
            </div>
            <Link
              to="/projects"
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 rounded-lg text-center font-medium hover:shadow-lg transition-all block"
            >
              프로젝트 보러가기
            </Link>
          </div>
        </div>

        {/* 메인 콘텐츠 */}
        <div className="flex-1 min-w-0 overflow-x-hidden">
          <div className="p-3 sm:p-4 lg:p-6 xl:p-8">
            <Routes>
              <Route path="/" element={<VipHome vipInfo={vipInfo} />} />
              <Route path="/rewards" element={<RewardHistory />} />
              <Route path="/withdrawals" element={<WithdrawalManagement />} />
              <Route path="/account" element={<AccountSettings />} />
              <Route path="/notices" element={<VipNotices />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

// 금액 포맷 함수
const formatCurrency = (amount) => {
  if (!amount) return '0원';
  return new Intl.NumberFormat('ko-KR').format(amount) + '원';
};

const VipHome = ({ vipInfo }) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  
  // 최근 프로젝트 가져오기
  const { data: recentProjects } = useQuery(
    'vip-recent-projects',
    () => api.get('/projects?limit=5').then(res => res.data)
  );


  // VIP 정보에서 수수료율 가져오기
  const effectiveFeeRate = vipInfo?.vipInfo?.effective_fee_rate || 0;

  const stats = [
    {
      title: '현재 잔액',
      value: formatCurrency(vipInfo?.vipInfo?.current_balance),
      icon: CurrencyDollarIcon,
      color: 'from-green-400 to-emerald-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700'
    },
    {
      title: '총 적립액',
      value: formatCurrency(vipInfo?.vipInfo?.total_earned),
      icon: TrophyIcon,
      color: 'from-blue-400 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700'
    },
    {
      title: '총 인출액',
      value: formatCurrency(vipInfo?.vipInfo?.total_withdrawn),
      icon: ArrowDownTrayIcon,
      color: 'from-purple-400 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700'
    },
    {
      title: '리워드 비율',
      value: `${vipInfo?.vipInfo?.reward_rate || 3}%`,
      icon: SparklesIcon,
      color: 'from-pink-400 to-pink-600',
      bgColor: 'bg-pink-50',
      textColor: 'text-pink-700'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* 히어로 섹션 */}
      <div className="relative mb-8">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-3xl blur-sm opacity-75"></div>
        <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/50 shadow-2xl">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* 환영 메시지 */}
            <div className="flex items-center gap-6">
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-3xl flex items-center justify-center shadow-lg">
                  <StarIcon className="h-10 w-10 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">V</span>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    {user?.name}님
                  </h1>
                  <div className="px-3 py-1 bg-gradient-to-r from-yellow-100 to-orange-100 border border-yellow-300 rounded-full">
                    <span className="text-xs font-bold text-yellow-700">VIP</span>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-xs sm:text-sm">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0"></div>
                    <span className="text-gray-600">리워드: <span className="font-semibold text-emerald-600">{vipInfo?.vipInfo?.reward_rate || 3}%</span></span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                    <span className="text-gray-600">프로젝트: <span className="font-semibold text-blue-600">{vipInfo?.completedProjects || 0}건</span></span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 현재 잔액 및 액션 버튼 */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-4 sm:p-6 border border-emerald-200 sm:min-w-[200px]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-emerald-700">현재 잔액</span>
                  <CurrencyDollarIcon className="h-5 w-5 text-emerald-600" />
                </div>
                <p className="text-xl sm:text-2xl font-bold text-emerald-900">
                  {formatCurrency(vipInfo?.vipInfo?.current_balance || 0)}
                </p>
                <p className="text-xs text-emerald-600 mt-1">즉시 인출 가능</p>
              </div>
              
              <div className="flex flex-col gap-2 w-full sm:w-auto">
                <Link
                  to="/vip/withdrawals"
                  className="group inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base"
                >
                  <BanknotesIcon className="h-4 w-4 sm:h-5 sm:w-5 group-hover:rotate-12 transition-transform duration-300" />
                  인출 신청
                </Link>
                <Link
                  to="/vip/rewards"
                  className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2 bg-white/80 text-indigo-600 rounded-2xl hover:bg-white transition-all duration-300 font-medium border border-indigo-200 hover:border-indigo-300 text-sm sm:text-base"
                >
                  <GiftIcon className="h-4 w-4" />
                  리워드 내역
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* 통계 대시보드 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          // 카드별 링크 설정
          let linkTo = "/projects";
          if (stat.title === "현재 잔액") linkTo = "/vip/withdrawals";
          else if (stat.title === "총 적립액") linkTo = "/vip/rewards?type=earn";
          else if (stat.title === "총 인출액") linkTo = "/vip/rewards?type=withdraw";
          else if (stat.title === "리워드 비율") linkTo = "/vip/account";
          
          return (
            <Link
              key={index}
              to={linkTo}
              className="group relative animate-fade-in-up block"
              style={{ 
                animationDelay: `${index * 150}ms`,
                animationDuration: '0.6s',
                animationFillMode: 'both'
              }}
            >
              <div className={`absolute inset-0 bg-gradient-to-r ${stat.color} rounded-2xl blur opacity-25 group-hover:opacity-40 transition-all duration-500`}></div>
              <div className="relative bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-white/50 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-2 sm:p-3 bg-gradient-to-br ${stat.bgColor} rounded-xl transform group-hover:rotate-6 transition-transform duration-300`}>
                    <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${stat.textColor}`} />
                  </div>
                  <div className="text-right">
                    <div className={`w-12 sm:w-16 h-1 bg-gradient-to-r ${stat.color} rounded-full mb-2 transform group-hover:scale-x-110 transition-transform duration-300`}></div>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 transition-colors duration-300 group-hover:text-gray-700">
                    {stat.value}
                  </p>
                  <div className="flex items-center gap-1">
                    <Icon className={`h-3 w-3 ${stat.textColor} transform group-hover:scale-110 transition-transform duration-300`} />
                    <span className={`text-xs ${stat.textColor} font-medium`}>
                      {stat.title === '현재 잔액' && '인출 가능'}
                      {stat.title === '총 적립액' && '누적 적립'}
                      {stat.title === '총 인출액' && '누적 인출'}
                      {stat.title === '리워드 비율' && '프리미엄 등급'}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* 메인 콘텐츠 영역 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-8">
        {/* 계좌 정보 - 확장된 디자인 */}
        <div className="lg:col-span-2">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-green-500 rounded-3xl blur-sm opacity-25"></div>
            <div className="relative bg-white rounded-3xl shadow-xl p-6 border border-white/50">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-emerald-100 to-green-100 rounded-2xl">
                    <CreditCardIcon className="h-6 w-6 text-emerald-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">계좌 정보</h2>
                </div>
                <Link
                  to="/vip/account"
                  className="inline-flex items-center gap-1 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-xl hover:bg-emerald-200 transition-colors font-medium text-sm"
                >
                  수정
                  <ChevronRightIcon className="h-4 w-4" />
                </Link>
              </div>
              
              {vipInfo?.vipInfo?.bank_name ? (
                <div className="space-y-4">
                  {/* 은행 정보 카드 */}
                  <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl p-5 border border-emerald-200">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <span className="text-xl">🏦</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-emerald-700">연결된 은행</p>
                        <p className="text-lg font-bold text-emerald-900">{vipInfo.vipInfo.bank_name}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="bg-white/60 rounded-xl p-3 sm:p-4">
                        <p className="text-xs font-medium text-emerald-700 mb-1">계좌번호</p>
                        <p className="font-mono font-bold text-gray-900 text-sm sm:text-base break-all">{vipInfo.vipInfo.bank_account}</p>
                      </div>
                      <div className="bg-white/60 rounded-xl p-3 sm:p-4">
                        <p className="text-xs font-medium text-emerald-700 mb-1">예금주</p>
                        <p className="font-bold text-gray-900 text-sm sm:text-base">{vipInfo.vipInfo.account_holder}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* 인출 안내 */}
                  <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-lg">💡</span>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-blue-800 mb-1">인출 안내</h4>
                        <ul className="text-xs text-blue-700 space-y-1">
                          <li>• 최소 인출 금액: 100,000원</li>
                          <li>• 처리 시간: 1-3 영업일</li>
                          <li>• 수수료: {effectiveFeeRate > 0 ? `${effectiveFeeRate}%` : '무료'}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-slate-100 rounded-3xl flex items-center justify-center mx-auto mb-4">
                    <CreditCardIcon className="h-10 w-10 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">계좌 정보 등록</h3>
                  <p className="text-gray-600 mb-6">리워드 인출을 위해 계좌 정보를 등록해주세요</p>
                  <Link
                    to="/vip/account"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-2xl hover:from-emerald-700 hover:to-green-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <CreditCardIcon className="h-5 w-5" />
                    계좌 등록하기
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 최근 프로젝트 - 개선된 디자인 */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-3xl blur-sm opacity-25"></div>
          <div className="relative bg-white rounded-3xl shadow-xl p-6 border border-white/50">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl">
                  <ClockIcon className="h-6 w-6 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">최근 프로젝트</h2>
              </div>
              <Link
                to="/projects"
                className="inline-flex items-center gap-1 px-4 py-2 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-colors font-medium text-sm"
              >
                전체보기
                <ChevronRightIcon className="h-4 w-4" />
              </Link>
            </div>
            
            {recentProjects?.projects?.length > 0 ? (
              <div className="space-y-4">
                {recentProjects.projects.slice(0, 3).map((project, index) => (
                  <Link
                    key={project.id}
                    to={`/projects/${project.id}`}
                    className="group block p-4 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 rounded-2xl transition-all duration-300 border border-transparent hover:border-blue-200"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      {/* 프로젝트 아이콘 */}
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                        project.status === 'completed' ? 'bg-green-100' :
                        project.status === 'progress' ? 'bg-yellow-100' :
                        project.status === 'contract' ? 'bg-blue-100' : 'bg-gray-100'
                      }`}>
                        <span className="text-lg sm:text-xl">
                          {project.status === 'completed' ? '✅' :
                           project.status === 'progress' ? '⚡' :
                           project.status === 'contract' ? '📋' : '📝'}
                        </span>
                      </div>
                      
                      {/* 프로젝트 정보 */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors text-sm sm:text-base">
                          {project.title}
                        </p>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-1">
                          <p className="text-xs sm:text-sm text-gray-500">
                            {format(new Date(project.created_at), 'MM월 dd일', { locale: ko })}
                          </p>
                          {project.budget && (
                            <>
                              <span className="text-sm text-gray-400 hidden sm:inline">•</span>
                              <p className="text-xs sm:text-sm font-medium text-green-600">
                                {formatCurrency(project.budget)}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* 상태 배지 */}
                      <div className={`px-2 sm:px-3 py-1 sm:py-2 rounded-full text-xs font-semibold flex items-center gap-1 ${
                        project.status === 'completed' ? 'bg-green-100 text-green-700' :
                        project.status === 'progress' ? 'bg-yellow-100 text-yellow-700' :
                        project.status === 'contract' ? 'bg-blue-100 text-blue-700' :
                        project.status === 'review' ? 'bg-purple-100 text-purple-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${
                          project.status === 'completed' ? 'bg-green-500' :
                          project.status === 'progress' ? 'bg-yellow-500' :
                          project.status === 'contract' ? 'bg-blue-500' :
                          project.status === 'review' ? 'bg-purple-500' :
                          'bg-gray-500'
                        }`}></div>
                        <span className="hidden sm:inline">
                          {project.status === 'completed' ? '완료' :
                           project.status === 'progress' ? '진행중' :
                           project.status === 'contract' ? '계약' :
                           project.status === 'review' ? '검토' : '접수'}
                        </span>
                        <span className="sm:hidden">
                          {project.status === 'completed' ? '완료' :
                           project.status === 'progress' ? '진행' :
                           project.status === 'contract' ? '계약' :
                           project.status === 'review' ? '검토' : '접수'}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
                
                {/* 더보기 버튼 */}
                {recentProjects.projects.length > 3 && (
                  <div className="pt-4 border-t border-gray-100">
                    <Link
                      to="/projects"
                      className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600 rounded-2xl hover:from-blue-100 hover:to-indigo-100 transition-all duration-300 font-medium border border-blue-200 hover:border-blue-300"
                    >
                      <span>더 많은 프로젝트 보기</span>
                      <ChevronRightIcon className="h-4 w-4" />
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-3xl flex items-center justify-center mx-auto mb-4">
                  <ClockIcon className="h-10 w-10 text-blue-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">첫 번째 프로젝트를 시작해보세요</h3>
                <p className="text-gray-600 mb-6">새로운 프로젝트를 등록하고 VIP 리워드를 받아보세요</p>
                <Link
                  to="/projects"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <PlusIcon className="h-5 w-5" />
                  프로젝트 등록하기
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 프로젝트 현황 카드 */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* 계약 카드 */}
        <Link
          to="/projects?status=contract"
          className="group relative bg-white rounded-2xl p-6 shadow-lg border border-amber-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
        >
          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">계약</p>
                <p className="text-3xl font-bold text-gray-900 mt-1 group-hover:scale-110 transition-transform origin-left">
                  {vipInfo?.projectStats?.contractCount || 0}
                </p>
                <p className="text-xs text-amber-600 mt-1">계약된 프로젝트</p>
              </div>
              <div className="bg-amber-200/50 p-3 rounded-xl group-hover:bg-amber-300/50 transition-colors">
                <ClockIcon className="h-6 w-6 text-amber-700" />
              </div>
            </div>
          </div>
        </Link>

        {/* 완료 카드 */}
        <Link
          to="/projects?status=completed"
          className="group relative bg-white rounded-2xl p-6 shadow-lg border border-green-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
        >
          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">완료</p>
                <p className="text-3xl font-bold text-gray-900 mt-1 group-hover:scale-110 transition-transform origin-left">
                  {vipInfo?.completedProjects || 0}
                </p>
                <p className="text-xs text-green-600 mt-1">성공적으로 완료한</p>
              </div>
              <div className="bg-green-200/50 p-3 rounded-xl group-hover:bg-green-300/50 transition-colors">
                <CheckCircleIcon className="h-6 w-6 text-green-700" />
              </div>
            </div>
          </div>
        </Link>

        {/* 전체 카드 */}
        <Link
          to="/projects"
          className="group relative bg-white rounded-2xl p-6 shadow-lg border border-blue-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
        >
          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">전체</p>
                <p className="text-3xl font-bold text-gray-900 mt-1 group-hover:scale-110 transition-transform origin-left">
                  {vipInfo?.projectStats?.totalCount || 0}
                </p>
                <p className="text-xs text-blue-600 mt-1">누적 프로젝트 수</p>
              </div>
              <div className="bg-blue-200/50 p-3 rounded-xl group-hover:bg-blue-300/50 transition-colors">
                <BriefcaseIcon className="h-6 w-6 text-blue-700" />
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* 월별 수익 차트 */}
      {vipInfo?.monthlyEarnings && vipInfo.monthlyEarnings.length > 0 && (
        <div className="mt-8">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-teal-400 to-cyan-500 rounded-3xl blur-sm opacity-25"></div>
            <div className="relative bg-white rounded-3xl shadow-xl p-4 sm:p-6 border border-white/50">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-2 sm:p-3 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-xl sm:rounded-2xl">
                    <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-teal-600" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">월별 수익 트렌드</h2>
                    <p className="text-xs sm:text-sm text-gray-600">최근 6개월 리워드 적립 현황</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs sm:text-sm text-gray-600">총 적립</p>
                  <p className="text-base sm:text-lg font-bold text-teal-600">
                    {formatCurrency(vipInfo?.vipInfo?.total_earned || 0)}
                  </p>
                </div>
              </div>
              
              {/* 차트 영역 - 반응형 개선 */}
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-4 sm:p-6">
                <div className="h-48 sm:h-64 relative">
                  <div className="absolute inset-0 flex items-end justify-between gap-1 sm:gap-2">
                    {vipInfo.monthlyEarnings.slice(-6).map((data, index) => {
                      const recentData = vipInfo.monthlyEarnings.slice(-6);
                      const maxAmount = Math.max(...recentData.map(d => d.amount));
                      const height = maxAmount > 0 ? (data.amount / maxAmount) * 100 : 0;
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center group">
                          <div className="w-full relative">
                            {/* 툴팁 - 모바일에서는 숨김 */}
                            <div className="hidden sm:block absolute -top-16 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                              {data.month}월: {formatCurrency(data.amount)}
                            </div>
                            
                            {/* 바 차트 */}
                            <div 
                              className="w-full bg-gradient-to-t from-teal-500 to-cyan-400 rounded-t-lg transition-all duration-700 hover:from-teal-600 hover:to-cyan-500"
                              style={{ 
                                height: `${height * 1.8}px`,
                                maxHeight: '180px',
                                minHeight: data.amount > 0 ? '8px' : '2px'
                              }}
                            />
                            
                            {/* 모바일에서 금액 표시 */}
                            {data.amount > 0 && (
                              <div className="sm:hidden absolute -top-6 left-1/2 transform -translate-x-1/2 text-[10px] font-bold text-teal-700 whitespace-nowrap">
                                {data.amount >= 1000000 ? 
                                  `${Math.floor(data.amount / 10000)}만` : 
                                  `${Math.floor(data.amount / 1000)}천`
                                }
                              </div>
                            )}
                          </div>
                          
                          {/* 월 표시 */}
                          <p className="text-[10px] sm:text-xs text-gray-600 mt-1 sm:mt-2 font-medium">{data.month}월</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              {/* 범례 및 통계 - 6개월 기준으로 수정 */}
              <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-teal-200">
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <div className="text-center">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-teal-500 rounded-full mx-auto mb-1 sm:mb-2"></div>
                    <p className="text-[10px] sm:text-xs text-gray-600 mb-0.5 sm:mb-1">6개월 평균</p>
                    <p className="text-xs sm:text-sm font-bold text-gray-900">
                      {formatCurrency(Math.round(vipInfo.monthlyEarnings.slice(-6).reduce((sum, d) => sum + d.amount, 0) / 6))}
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-cyan-400 rounded-full mx-auto mb-1 sm:mb-2"></div>
                    <p className="text-[10px] sm:text-xs text-gray-600 mb-0.5 sm:mb-1">최고 월</p>
                    <p className="text-xs sm:text-sm font-bold text-gray-900">
                      {formatCurrency(Math.max(...vipInfo.monthlyEarnings.slice(-6).map(d => d.amount)))}
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-gradient-to-r from-teal-500 to-cyan-400 rounded-full mx-auto mb-1 sm:mb-2"></div>
                    <p className="text-[10px] sm:text-xs text-gray-600 mb-0.5 sm:mb-1">리워드율</p>
                    <p className="text-xs sm:text-sm font-bold text-gray-900">{vipInfo?.vipInfo?.reward_rate || 3}%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 추가 액션 버튼들 */}
      <div className="mt-8 mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link
            to="/vip/account"
            className="group relative overflow-hidden bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-4 sm:p-6 text-white hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3">
                <CreditCardIcon className="h-6 w-6 sm:h-8 sm:w-8" />
                <div>
                  <h3 className="text-base sm:text-lg font-bold">계좌 설정</h3>
                  <p className="text-xs sm:text-sm text-white/80">인출 계좌 관리</p>
                </div>
              </div>
              <p className="text-white/90 text-xs sm:text-sm">
                빠른 인출을 위해 계좌 정보를 확인하고 업데이트하세요
              </p>
            </div>
            <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-full -translate-y-12 translate-x-12 sm:-translate-y-16 sm:translate-x-16 group-hover:scale-110 transition-transform duration-300"></div>
          </Link>
          
          <Link
            to="/projects"
            className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-4 sm:p-6 text-white hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3">
                <PlusIcon className="h-6 w-6 sm:h-8 sm:w-8" />
                <div>
                  <h3 className="text-base sm:text-lg font-bold">새 프로젝트</h3>
                  <p className="text-xs sm:text-sm text-white/80">리워드 적립 기회</p>
                </div>
              </div>
              <p className="text-white/90 text-xs sm:text-sm">
                새로운 프로젝트를 등록하고 {vipInfo?.vipInfo?.reward_rate || 3}% 리워드를 받으세요
              </p>
            </div>
            <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-full -translate-y-12 translate-x-12 sm:-translate-y-16 sm:translate-x-16 group-hover:scale-110 transition-transform duration-300"></div>
          </Link>
        </div>
      </div>
    </div>
  );
};

const RewardHistory = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState('all'); // all, earn, withdraw
  const [hasMore, setHasMore] = useState(true);
  const [allRewards, setAllRewards] = useState([]);
  const observerRef = React.useRef();
  const loadingRef = React.useRef(false);

  const { data: vipInfo } = useQuery(
    'vip-info',
    () => api.get('/vip/info').then(res => res.data)
  );

  const { data, isLoading, refetch } = useQuery(
    ['vip-rewards', currentPage, filter],
    () => api.get('/vip/rewards', { 
      params: { 
        page: currentPage, 
        limit: 20,
        type: filter === 'all' ? undefined : filter
      }
    }).then(res => res.data),
    {
      keepPreviousData: true,
      onSuccess: (data) => {
        if (currentPage === 1) {
          setAllRewards(data.rewards || []);
        } else {
          setAllRewards(prev => [...prev, ...(data.rewards || [])]);
        }
        setHasMore(currentPage < data.pagination?.totalPages);
      }
    }
  );

  // 무한 스크롤 Intersection Observer
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
          loadingRef.current = true;
          setCurrentPage(prev => prev + 1);
          setTimeout(() => {
            loadingRef.current = false;
          }, 500);
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore]);

  // 필터 변경 시 초기화
  React.useEffect(() => {
    setCurrentPage(1);
    setAllRewards([]);
    setHasMore(true);
    // 필터 변경 시 즉시 새 데이터 요청
    refetch();
  }, [filter, refetch]);

  const stats = React.useMemo(() => {
    if (!allRewards.length) return { totalEarned: 0, totalWithdrawn: 0 };
    
    const totalEarned = allRewards
      .filter(r => r.transaction_type === 'earn')
      .reduce((sum, r) => sum + r.amount, 0);
    
    const totalWithdrawn = allRewards
      .filter(r => r.transaction_type === 'withdraw')
      .reduce((sum, r) => sum + r.amount, 0);

    return { totalEarned, totalWithdrawn };
  }, [allRewards]);

  if (isLoading && currentPage === 1) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-32 bg-gray-200 rounded-xl"></div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-gray-100 rounded-lg"></div>
          ))}
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-20 bg-gray-100 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-3 lg:px-4">
      {/* 헤더 및 통계 */}
      <div className="mb-6 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-4 sm:p-6 lg:p-8 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold flex items-center gap-3 mb-2">
              <GiftIcon className="h-6 w-6 sm:h-8 sm:w-8" />
              리워드 내역
            </h1>
            <p className="text-white/80 text-sm sm:text-base">
              총 {data?.pagination?.totalCount || 0}건의 리워드 내역이 있습니다
            </p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-white/80 mb-1">현재 잔액</p>
            <p className="text-lg sm:text-2xl lg:text-3xl font-bold">
              {formatCurrency(vipInfo?.vipInfo?.current_balance)}
            </p>
          </div>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 mb-1">총 적립</p>
              <p className="text-lg sm:text-xl font-bold text-green-600">
                +{formatCurrency(vipInfo?.vipInfo?.total_earned || 0)}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-green-100 rounded-lg">
              <ArrowUpTrayIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 mb-1">총 인출</p>
              <p className="text-lg sm:text-xl font-bold text-red-600">
                {formatCurrency(vipInfo?.vipInfo?.total_withdrawn || 0)}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-red-100 rounded-lg">
              <ArrowDownTrayIcon className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 mb-1">리워드율</p>
              <p className="text-lg sm:text-xl font-bold text-purple-600">
                {vipInfo?.vipInfo?.reward_rate || 3}%
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-purple-100 rounded-lg">
              <SparklesIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* 필터 탭 */}
      <div className="flex gap-1 sm:gap-2 mb-6 bg-white rounded-xl p-1 shadow-sm">
        {[
          { value: 'all', label: '전체', count: data?.counts?.all },
          { value: 'earn', label: '적립', icon: ArrowUpTrayIcon, color: 'text-green-600', count: data?.counts?.earn },
          { value: 'withdraw', label: '인출', icon: ArrowDownTrayIcon, color: 'text-red-600', count: data?.counts?.withdraw }
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value)}
            className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 sm:py-3 rounded-lg font-medium transition-all text-xs sm:text-sm ${
              filter === tab.value
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {tab.icon && <tab.icon className={`h-4 w-4 sm:h-5 sm:w-5 ${filter === tab.value ? 'text-white' : tab.color}`} />}
            <span>{tab.label}</span>
            {tab.count !== undefined && (
              <span className={`text-xs ${filter === tab.value ? 'text-white/80' : 'text-gray-400'}`}>
                ({tab.count})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* 리워드 리스트 */}
      <div className="space-y-3">
        {allRewards.length > 0 ? (
          <>
            {allRewards.map((reward) => (
              <div key={reward.id} className="bg-white rounded-xl shadow-sm p-3 sm:p-5 hover:shadow-md transition-shadow border border-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                  <div className="flex gap-3">
                    <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${
                      reward.transaction_type === 'earn' 
                        ? 'bg-gradient-to-br from-green-100 to-emerald-100' 
                        : 'bg-gradient-to-br from-red-100 to-pink-100'
                    }`}>
                      {reward.transaction_type === 'earn' ? (
                        <ArrowUpTrayIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                      ) : (
                        <ArrowDownTrayIcon className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">
                        {reward.description}
                      </p>
                      {reward.project_title && (
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">
                          프로젝트: {reward.project_title}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        {format(new Date(reward.created_at), 'MM/dd HH:mm', { locale: ko })}
                      </p>
                    </div>
                  </div>
                  <div className="text-left sm:text-right bg-gray-50 sm:bg-transparent p-3 sm:p-0 rounded-lg sm:rounded-none">
                    <p className={`text-lg sm:text-xl font-bold mb-1 ${
                      reward.transaction_type === 'earn' 
                        ? 'text-green-600' 
                        : 'text-red-600'
                    }`}>
                      {reward.transaction_type === 'earn' ? '+' : '-'}
                      {formatCurrency(reward.amount)}
                    </p>
                    {reward.balance_after && (
                      <p className="text-xs text-gray-500">
                        잔액: {formatCurrency(reward.balance_after)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* 무한 스크롤 로딩 */}
            {hasMore && (
              <div ref={observerRef} className="flex justify-center py-8">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                  <span className="text-sm text-gray-500">더 불러오는 중...</span>
                </div>
              </div>
            )}
          </>
        ) : !isLoading && data && data.rewards && data.rewards.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-16 text-center">
            <GiftIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">리워드 내역이 없습니다</p>
            <p className="text-sm text-gray-400">
              프로젝트 완료 시 리워드가 자동으로 적립됩니다
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const WithdrawalManagement = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [feeInfo, setFeeInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState('all'); // all, pending, approved, completed, rejected
  const [hasMore, setHasMore] = useState(true);
  const [allWithdrawals, setAllWithdrawals] = useState([]);
  const observerRef = React.useRef();
  const loadingRef = React.useRef(false);

  const { data: vipInfo } = useQuery(
    'vip-info',
    () => api.get('/vip/info').then(res => res.data)
  );

  const { data, isLoading, refetch } = useQuery(
    ['vip-withdrawals', currentPage, filter],
    () => api.get('/vip/withdrawals', { 
      params: { 
        page: currentPage, 
        limit: 20,
        status: filter === 'all' ? undefined : filter
      }
    }).then(res => res.data),
    {
      keepPreviousData: true,
      onSuccess: (data) => {
        if (currentPage === 1) {
          setAllWithdrawals(data.withdrawals || []);
        } else {
          setAllWithdrawals(prev => [...prev, ...(data.withdrawals || [])]);
        }
        setHasMore(currentPage < data.pagination?.totalPages);
      }
    }
  );

  const withdrawMutation = useMutation(
    (data) => api.post('/vip/withdrawals', data),
    {
      onSuccess: (response) => {
        const feeInfo = response.data?.feeInfo;
        if (feeInfo && feeInfo.feeAmount > 0) {
          toast.success(`인출 신청이 완료되었습니다. (수수료: ${feeInfo.feeAmount.toLocaleString()}원, 실지급액: ${feeInfo.netAmount.toLocaleString()}원)`);
        } else {
          toast.success('인출 신청이 완료되었습니다.');
        }
        queryClient.invalidateQueries('vip-info');
        queryClient.invalidateQueries('vip-withdrawals');
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        setFeeInfo(null);
      },
      onError: (error) => {
        const errorData = error.response?.data;
        const errorMessage = errorData?.error || '인출 신청에 실패했습니다.';
        const errorDetails = errorData?.details;
        
        if (errorMessage === '계좌 정보를 먼저 등록해주세요') {
          // 계좌 정보 미등록 시 안내 모달 표시
          toast.error(
            <div>
              <p className="font-semibold">{errorMessage}</p>
              {errorDetails && <p className="text-sm mt-1">{errorDetails}</p>}
              <button 
                onClick={() => navigate('/vip/account')}
                className="mt-2 text-xs bg-white/20 px-2 py-1 rounded hover:bg-white/30"
              >
                계좌 등록하러 가기
              </button>
            </div>,
            { autoClose: 8000 }
          );
        } else if (errorDetails) {
          toast.error(
            <div>
              <p className="font-semibold">{errorMessage}</p>
              <p className="text-sm mt-1">{errorDetails}</p>
            </div>
          );
        } else {
          toast.error(errorMessage);
        }
      }
    }
  );

  // 무한 스크롤 Intersection Observer
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
          loadingRef.current = true;
          setCurrentPage(prev => prev + 1);
          setTimeout(() => {
            loadingRef.current = false;
          }, 500);
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore]);

  // 필터 변경 시 초기화  
  React.useEffect(() => {
    setCurrentPage(1);
    setAllWithdrawals([]);
    setHasMore(true);
    // 필터 변경 시 즉시 새 데이터 요청
    refetch();
  }, [filter, refetch]);

  // 수수료 계산 함수
  const calculateFeeInfo = async (amount) => {
    if (!amount || amount < 100000) {
      setFeeInfo(null);
      return;
    }

    try {
      // 임시로 3.3% 기본 수수료율로 계산 (실제로는 API에서 받아야 함)
      const response = await api.get('/vip/fee-settings');
      const feeRate = response.data?.defaultFeeRate || 0;
      
      const feeAmount = Math.floor(amount * (feeRate / 100));
      const netAmount = amount - feeAmount;

      setFeeInfo({
        requestedAmount: amount,
        feeRate: feeRate,
        feeAmount: feeAmount,
        netAmount: netAmount
      });
    } catch (error) {
      console.error('Fee calculation error:', error);
      setFeeInfo(null);
    }
  };

  // 인출 금액 변경 시 수수료 계산
  React.useEffect(() => {
    const amount = parseInt(withdrawAmount.replace(/,/g, ''));
    if (amount && amount >= 100000) {
      calculateFeeInfo(amount);
    } else {
      setFeeInfo(null);
    }
  }, [withdrawAmount]);

  const handleWithdraw = () => {
    const amount = parseInt(withdrawAmount.replace(/,/g, ''));
    if (!amount || amount < 100000) {
      toast.error('최소 인출 금액은 100,000원입니다.');
      return;
    }
    if (amount > vipInfo?.vipInfo?.current_balance) {
      toast.error('잔액이 부족합니다.');
      return;
    }
    withdrawMutation.mutate({ amount });
  };

  const statusConfig = {
    pending: { label: '검토중', color: 'text-yellow-600 bg-yellow-100', icon: ClockIcon },
    approved: { label: '승인', color: 'text-blue-600 bg-blue-100', icon: CheckCircleIcon },
    completed: { label: '완료', color: 'text-green-600 bg-green-100', icon: CheckCircleIcon },
    rejected: { label: '거절', color: 'text-red-600 bg-red-100', icon: XCircleIcon }
  };

  // 상태별 개수 계산
  const statusCounts = React.useMemo(() => {
    return allWithdrawals.reduce((acc, w) => {
      acc[w.status] = (acc[w.status] || 0) + 1;
      return acc;
    }, {});
  }, [allWithdrawals]);

  if (isLoading && currentPage === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-3 lg:px-4 py-4 sm:py-6">
          <div className="animate-pulse space-y-8">
            {/* 헤더 스켈레톤 */}
            <div className="bg-white/50 rounded-3xl p-8 border border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gray-300 rounded-2xl"></div>
                  <div>
                    <div className="h-8 bg-gray-300 rounded-lg w-32 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-48"></div>
                  </div>
                </div>
                <div className="h-12 bg-gray-300 rounded-2xl w-32"></div>
              </div>
            </div>
            
            {/* 통계 카드 스켈레톤 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-gray-300 rounded-xl"></div>
                    <div className="w-16 h-1 bg-gray-300 rounded-full"></div>
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                  <div className="h-8 bg-gray-300 rounded w-32 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-24"></div>
                </div>
              ))}
            </div>
            
            {/* 필터 스켈레톤 */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100">
              <div className="h-6 bg-gray-300 rounded w-40 mb-4"></div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="bg-gray-100 rounded-xl p-4 border-2 border-gray-200">
                    <div className="text-center">
                      <div className="w-8 h-8 bg-gray-300 rounded mx-auto mb-2"></div>
                      <div className="h-4 bg-gray-300 rounded w-12 mx-auto mb-1"></div>
                      <div className="h-6 bg-gray-300 rounded w-8 mx-auto"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* 리스트 스켈레톤 */}
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="h-1 bg-gray-300 rounded-full mb-6"></div>
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-300 rounded-xl"></div>
                      <div>
                        <div className="h-6 bg-gray-300 rounded w-20 mb-2"></div>
                        <div className="h-8 bg-gray-300 rounded w-32"></div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="h-4 bg-gray-200 rounded w-12 mb-1"></div>
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-full"></div>
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-full"></div>
                        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                        <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-2 sm:px-3 lg:px-4 py-4 sm:py-6">
        {/* 헤더 섹션 */}
        <div className="relative mb-8">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-3xl blur-sm opacity-75"></div>
          <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/50 shadow-2xl">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg">
                  <BanknotesIcon className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    인출 관리
                  </h1>
                  <p className="text-gray-600 mt-1">
                    총 <span className="font-semibold text-indigo-600">{data?.pagination?.totalCount || 0}건</span>의 인출 신청
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl p-4 border border-emerald-200">
                  <p className="text-xs font-medium text-emerald-700 mb-1">현재 잔액</p>
                  <p className="text-lg font-bold text-emerald-900">
                    {formatCurrency(vipInfo?.vipInfo?.current_balance || 0)}
                  </p>
                </div>
                
                <button
                  onClick={() => setShowWithdrawModal(true)}
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <PlusIcon className="h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
                  인출 신청
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 통계 대시보드 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {/* 총 인출액 카드 */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-rose-400 to-pink-500 rounded-2xl blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
            <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-rose-100 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-rose-100 to-pink-100 rounded-xl">
                  <ArrowDownTrayIcon className="h-6 w-6 text-rose-600" />
                </div>
                <div className="text-right">
                  <div className="w-16 h-1 bg-gradient-to-r from-rose-400 to-pink-500 rounded-full mb-2"></div>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">총 인출액</p>
                <p className="text-2xl font-bold text-gray-900 mb-2">
                  {formatCurrency(data?.totalWithdrawn || 0)}
                </p>
                <p className="text-xs text-rose-600 flex items-center gap-1">
                  <ArrowDownTrayIcon className="h-3 w-3" />
                  누적 인출 금액
                </p>
              </div>
            </div>
          </div>

          {/* 현재 잔액 카드 */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-2xl blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
            <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-blue-100 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl">
                  <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="text-right">
                  <div className="w-16 h-1 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full mb-2"></div>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">현재 잔액</p>
                <p className="text-2xl font-bold text-gray-900 mb-2">
                  {formatCurrency(vipInfo?.vipInfo?.current_balance || 0)}
                </p>
                <p className="text-xs text-blue-600 flex items-center gap-1">
                  <CurrencyDollarIcon className="h-3 w-3" />
                  사용 가능 금액
                </p>
              </div>
            </div>
          </div>

          {/* 인출 가능액 카드 */}
          <div className="group relative sm:col-span-2 lg:col-span-1">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-green-500 rounded-2xl blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
            <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-emerald-100 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl">
                  <CheckCircleIcon className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="text-right">
                  <div className="w-16 h-1 bg-gradient-to-r from-emerald-400 to-green-500 rounded-full mb-2"></div>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">인출 가능액</p>
                <p className="text-2xl font-bold text-gray-900 mb-2">
                  {formatCurrency(vipInfo?.vipInfo?.current_balance || 0)}
                </p>
                <p className="text-xs text-emerald-600 flex items-center gap-1">
                  <CheckCircleIcon className="h-3 w-3" />
                  즉시 인출 가능
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 필터 탭 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5 mb-4">
          <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">인출 상태별 필터</h2>
          
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {/* 전체 버튼 - 모바일에서 전체 너비 */}
            <button
              onClick={() => setFilter('all')}
              className={`col-span-2 sm:col-span-1 flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                filter === 'all'
                  ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-base">📊</span>
                <span className="font-medium text-sm">전체</span>
              </div>
              <span className="font-bold text-sm">{data?.pagination?.totalCount || 0}</span>
            </button>

            {/* 검토중 버튼 */}
            <button
              onClick={() => setFilter('pending')}
              className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                filter === 'pending'
                  ? 'bg-amber-50 border-amber-300 text-amber-700'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-base">⏳</span>
                <span className="font-medium text-sm">검토중</span>
              </div>
              <span className="font-bold text-sm ml-1">{statusCounts.pending || 0}</span>
            </button>

            {/* 승인 버튼 */}
            <button
              onClick={() => setFilter('approved')}
              className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                filter === 'approved'
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-base">✅</span>
                <span className="font-medium text-sm">승인</span>
              </div>
              <span className="font-bold text-sm ml-1">{statusCounts.approved || 0}</span>
            </button>

            {/* 완료 버튼 */}
            <button
              onClick={() => setFilter('completed')}
              className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                filter === 'completed'
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-base">💰</span>
                <span className="font-medium text-sm">완료</span>
              </div>
              <span className="font-bold text-sm ml-1">{statusCounts.completed || 0}</span>
            </button>

            {/* 거절 버튼 */}
            <button
              onClick={() => setFilter('rejected')}
              className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                filter === 'rejected'
                  ? 'bg-red-50 border-red-300 text-red-700'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-base">❌</span>
                <span className="font-medium text-sm">거절</span>
              </div>
              <span className="font-bold text-sm ml-1">{statusCounts.rejected || 0}</span>
            </button>
          </div>
        </div>

        {/* 인출 내역 리스트 */}
        <div className="space-y-4">
          {allWithdrawals.length > 0 ? (
            <>
              {allWithdrawals.map((withdrawal, index) => {
                const StatusIcon = statusConfig[withdrawal.status].icon;
                const getStatusStyle = (status) => {
                  switch (status) {
                    case 'completed':
                      return {
                        bgGradient: 'from-emerald-500 to-green-600',
                        bgLight: 'from-emerald-50 to-green-50',
                        border: 'border-emerald-200',
                        text: 'text-emerald-700',
                        emoji: '💰'
                      };
                    case 'pending':
                      return {
                        bgGradient: 'from-amber-500 to-yellow-600',
                        bgLight: 'from-amber-50 to-yellow-50',
                        border: 'border-amber-200',
                        text: 'text-amber-700',
                        emoji: '⏳'
                      };
                    case 'approved':
                      return {
                        bgGradient: 'from-blue-500 to-indigo-600',
                        bgLight: 'from-blue-50 to-indigo-50',
                        border: 'border-blue-200',
                        text: 'text-blue-700',
                        emoji: '✅'
                      };
                    case 'rejected':
                      return {
                        bgGradient: 'from-red-500 to-rose-600',
                        bgLight: 'from-red-50 to-rose-50',
                        border: 'border-red-200',
                        text: 'text-red-700',
                        emoji: '❌'
                      };
                    default:
                      return {
                        bgGradient: 'from-gray-500 to-slate-600',
                        bgLight: 'from-gray-50 to-slate-50',
                        border: 'border-gray-200',
                        text: 'text-gray-700',
                        emoji: '📋'
                      };
                  }
                };

                const statusStyle = getStatusStyle(withdrawal.status);

                return (
                  <div 
                    key={withdrawal.id} 
                    className="group relative bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {/* 상태 표시 라인 */}
                    <div className={`h-1 bg-gradient-to-r ${statusStyle.bgGradient}`}></div>
                    
                    <div className="p-6">
                      {/* 헤더 섹션 */}
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-xl bg-gradient-to-br ${statusStyle.bgLight} ${statusStyle.border} border`}>
                            <div className="text-2xl">{statusStyle.emoji}</div>
                          </div>
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r ${statusStyle.bgLight} ${statusStyle.text} ${statusStyle.border} border`}>
                                {statusConfig[withdrawal.status].label}
                              </span>
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                #{withdrawal.id}
                              </span>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">
                              {formatCurrency(withdrawal.amount)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-xs text-gray-500 mb-1">신청일</p>
                          <p className="text-sm font-medium text-gray-700">
                            {format(new Date(withdrawal.requested_at), 'MM/dd', { locale: ko })}
                          </p>
                          <p className="text-xs text-gray-500">
                            {format(new Date(withdrawal.requested_at), 'HH:mm', { locale: ko })}
                          </p>
                        </div>
                      </div>

                      {/* 상세 정보 그리드 */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* 처리 정보 */}
                        <div className="space-y-3">
                          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                            처리 정보
                          </h4>
                          <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-4 space-y-2">
                            {withdrawal.processed_at ? (
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">처리일</span>
                                <span className="text-sm font-medium text-gray-900">
                                  {format(new Date(withdrawal.processed_at), 'MM/dd HH:mm', { locale: ko })}
                                </span>
                              </div>
                            ) : null}
                            {withdrawal.processor_name ? (
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">처리자</span>
                                <span className="text-sm font-medium text-gray-900">
                                  {withdrawal.processor_name}
                                </span>
                              </div>
                            ) : null}
                            {!withdrawal.processed_at && withdrawal.status === 'pending' ? (
                              <div className="text-center py-2">
                                <span className="text-sm text-amber-600 font-medium">검토 대기 중</span>
                              </div>
                            ) : null}
                          </div>
                        </div>

                        {/* 계좌 정보 */}
                        <div className="space-y-3">
                          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                            계좌 정보
                          </h4>
                          <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100">
                            <div className="space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">은행</span>
                                <span className="text-sm font-medium text-gray-900">{withdrawal.bank_name}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">계좌번호</span>
                                <span className="text-sm font-mono font-medium text-gray-900 break-all">
                                  {withdrawal.bank_account}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">예금주</span>
                                <span className="text-sm font-medium text-gray-900">{withdrawal.account_holder}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                      </div>

                      {/* 수수료 정보 */}
                      {(withdrawal.fee_amount > 0 || withdrawal.fee_rate > 0) ? (
                          <div className="space-y-3">
                            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                              <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                              수수료 정보
                            </h4>
                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-600">인출 신청액</span>
                                  <span className="text-sm font-medium text-gray-900">
                                    {formatCurrency(withdrawal.amount)}
                                  </span>
                                </div>
                                {withdrawal.fee_amount > 0 ? (
                                  <>
                                    <div className="flex justify-between items-center">
                                      <span className="text-sm text-gray-600">
                                        수수료 ({withdrawal.fee_rate}%)
                                      </span>
                                      <span className="text-sm font-medium text-red-600">
                                        -{formatCurrency(withdrawal.fee_amount)}
                                      </span>
                                    </div>
                                    <hr className="border-purple-200" />
                                    <div className="flex justify-between items-center">
                                      <span className="text-sm font-semibold text-purple-800">실 지급액</span>
                                      <span className="text-sm font-bold text-green-600">
                                        {formatCurrency(withdrawal.net_amount)}
                                      </span>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">수수료</span>
                                    <span className="text-sm font-medium text-green-600">
                                      무료 (0%)
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                      ) : null}

                      {/* 거절 사유 */}
                      {withdrawal.reject_reason && (
                        <div className="mt-6 p-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-xl">
                          <div className="flex items-start gap-3">
                            <div className="p-1 bg-red-100 rounded-lg">
                              <XCircleIcon className="h-4 w-4 text-red-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-red-800 mb-1">거절 사유</p>
                              <p className="text-sm text-red-700">{withdrawal.reject_reason}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 메모 */}
                      {withdrawal.memo && (
                        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                          <div className="flex items-start gap-3">
                            <div className="p-1 bg-blue-100 rounded-lg">
                              <span className="text-sm">📝</span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-blue-800 mb-1">처리 메모</p>
                              <p className="text-sm text-blue-700">{withdrawal.memo}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            
              {/* 무한 스크롤 로딩 */}
              {hasMore && (
                <div ref={observerRef} className="flex justify-center py-12">
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 px-8 py-6">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200"></div>
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent absolute top-0"></div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">더 많은 인출 내역을 불러오는 중...</p>
                        <p className="text-xs text-gray-500">잠시만 기다려주세요</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* 마지막 페이지 표시 */}
              {!hasMore && allWithdrawals.length > 0 && (
                <div className="text-center py-8">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-100 to-slate-100 rounded-full">
                    <span className="text-sm text-gray-600">모든 인출 내역을 확인했습니다</span>
                    <span className="text-lg">✨</span>
                  </div>
                </div>
              )}
            </>
          ) : !isLoading && data && data.withdrawals && data.withdrawals.length === 0 ? (
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-100 via-purple-50 to-pink-100 rounded-3xl blur-sm opacity-60"></div>
              <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl p-12 text-center border border-white/50 shadow-xl">
                <div className="mb-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-3xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-4xl">💸</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {filter === 'all' ? '인출 내역이 없습니다' : `${
                    filter === 'pending' ? '검토 중인' :
                    filter === 'approved' ? '승인된' :
                    filter === 'completed' ? '완료된' :
                    filter === 'rejected' ? '거절된' : ''
                  } 인출 내역이 없습니다`}
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  {filter === 'all' 
                    ? '아직 인출 신청을 하지 않으셨네요. 인출 신청을 통해 리워드를 받아보세요!'
                    : '해당 상태의 인출 내역이 없습니다. 다른 필터를 선택해보세요.'
                  }
                </p>
                {filter === 'all' && (
                  <button
                    onClick={() => setShowWithdrawModal(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <PlusIcon className="h-5 w-5" />
                    첫 인출 신청하기
                  </button>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* 인출 신청 모달 */}
        {showWithdrawModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
            <div className="relative z-[10000]">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl blur-sm opacity-20"></div>
              <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl max-w-md w-full border border-white/50 shadow-2xl flex flex-col max-h-[90vh]">
                {/* 헤더 - 고정 */}
                <div className="p-8 pb-4 text-center flex-shrink-0 relative">
                  <button
                    onClick={() => {
                      setShowWithdrawModal(false);
                      setWithdrawAmount('');
                    }}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">💰</span>
                  </div>
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    인출 신청
                  </h2>
                  <p className="text-gray-600 text-sm mt-2">
                    리워드를 안전하게 인출해보세요
                  </p>
                </div>
                
                {/* 콘텐츠 - 스크롤 가능 */}
                <div className="px-8 py-4 overflow-y-auto flex-1">
                  <div className="space-y-6">
                  {/* 현재 잔액 표시 */}
                  <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl p-4 border border-emerald-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-emerald-700">현재 잔액</p>
                        <p className="text-xl font-bold text-emerald-900">
                          {formatCurrency(vipInfo?.vipInfo?.current_balance || 0)}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <CurrencyDollarIcon className="h-6 w-6 text-emerald-600" />
                      </div>
                    </div>
                  </div>

                  {/* 인출 금액 입력 */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      인출 금액
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                        ₩
                      </span>
                      <input
                        type="text"
                        value={withdrawAmount}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          if (value) {
                            setWithdrawAmount(parseInt(value).toLocaleString());
                          } else {
                            setWithdrawAmount('');
                          }
                        }}
                        placeholder="100,000"
                        className="w-full pl-12 pr-4 py-4 text-lg font-semibold border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all bg-white/80"
                      />
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-500">
                        최소 인출: 100,000원
                      </p>
                      <button
                        onClick={() => setWithdrawAmount((vipInfo?.vipInfo?.current_balance || 0).toLocaleString())}
                        className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        전액 인출
                      </button>
                    </div>
                  </div>

                  {/* 계좌 정보 */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="text-lg">🏦</span>
                      </div>
                      <h4 className="text-sm font-semibold text-blue-800">입금받을 계좌</h4>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-blue-700">은행</span>
                        <span className="text-sm font-semibold text-blue-900">
                          {vipInfo?.vipInfo?.bank_name || '계좌 정보 없음'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-blue-700">계좌번호</span>
                        <span className="text-sm font-mono font-semibold text-blue-900">
                          {vipInfo?.vipInfo?.bank_account || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-blue-700">예금주</span>
                        <span className="text-sm font-semibold text-blue-900">
                          {vipInfo?.vipInfo?.account_holder || '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 수수료 정보 */}
                  {feeInfo && (
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-5 border border-purple-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <span className="text-lg">💰</span>
                        </div>
                        <h4 className="text-sm font-semibold text-purple-800">수수료 정보</h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-purple-700">인출 신청액</span>
                          <span className="text-sm font-semibold text-purple-900">
                            {feeInfo.requestedAmount.toLocaleString()}원
                          </span>
                        </div>
                        {feeInfo.feeAmount > 0 ? (
                          <>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-purple-700">수수료 ({feeInfo.feeRate}%)</span>
                              <span className="text-sm font-semibold text-red-600">
                                -{feeInfo.feeAmount.toLocaleString()}원
                              </span>
                            </div>
                            <hr className="border-purple-200" />
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-semibold text-purple-800">실 지급액</span>
                              <span className="text-sm font-bold text-green-600">
                                {feeInfo.netAmount.toLocaleString()}원
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-purple-700">수수료</span>
                            <span className="text-sm font-semibold text-green-600">
                              무료 (0%)
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* 주의사항 */}
                  <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-sm">⚠️</span>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-amber-800 mb-1">인출 안내</h4>
                        <ul className="text-xs text-amber-700 space-y-1">
                          <li>• 인출 신청 후 1-3 영업일 내 처리됩니다</li>
                          <li>• 계좌 정보를 다시 한번 확인해주세요</li>
                          <li>• 처리 완료 시 알림을 보내드립니다</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>

                {/* 푸터/버튼 - 고정 */}
                <div className="p-8 pt-4 border-t border-gray-100 flex-shrink-0">
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowWithdrawModal(false);
                        setWithdrawAmount('');
                      }}
                      className="flex-1 px-6 py-4 bg-gray-100 text-gray-700 rounded-2xl hover:bg-gray-200 transition-all duration-200 font-semibold"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleWithdraw}
                      disabled={withdrawMutation.isLoading || !withdrawAmount}
                      className="flex-1 px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none disabled:hover:shadow-lg"
                    >
                      {withdrawMutation.isLoading ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          처리중...
                        </div>
                      ) : (
                        '인출 신청'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AccountSettings = () => {
  const queryClient = useQueryClient();
  const { data: vipInfo } = useQuery(
    'vip-info',
    () => api.get('/vip/info').then(res => res.data)
  );

  const [formData, setFormData] = useState({
    bank_name: '',
    bank_account: '',
    account_holder: ''
  });
  
  const [showCustomBank, setShowCustomBank] = useState(false);
  const [customBankName, setCustomBankName] = useState('');

  React.useEffect(() => {
    if (vipInfo?.vipInfo) {
      const bankName = vipInfo.vipInfo.bank_name || '';
      // 미리 정의된 은행 목록에 없는 경우 "기타"로 설정
      const predefinedBanks = [
        'KB국민은행', '신한은행', '하나은행', '우리은행', 'SC제일은행',
        '한국씨티은행', '카카오뱅크', '토스뱅크', '새마을금고', '신협',
        '우체국', '기업은행', '농협은행', '수협은행'
      ];
      
      if (bankName && !predefinedBanks.includes(bankName)) {
        setShowCustomBank(true);
        setCustomBankName(bankName);
        setFormData({
          bank_name: '기타',
          bank_account: vipInfo.vipInfo.bank_account || '',
          account_holder: vipInfo.vipInfo.account_holder || ''
        });
      } else {
        setFormData({
          bank_name: bankName,
          bank_account: vipInfo.vipInfo.bank_account || '',
          account_holder: vipInfo.vipInfo.account_holder || ''
        });
      }
    }
  }, [vipInfo]);

  const updateAccountMutation = useMutation(
    (data) => api.put('/vip/account', data),
    {
      onSuccess: () => {
        toast.success('계좌 정보가 업데이트되었습니다.');
        queryClient.invalidateQueries('vip-info');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '업데이트에 실패했습니다.');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      bank_name: showCustomBank ? customBankName : formData.bank_name
    };
    updateAccountMutation.mutate(submitData);
  };

  return (
    <div>
      <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <CreditCardIcon className="h-6 w-6 lg:h-8 lg:w-8 text-purple-600" />
        계좌 정보 설정
      </h1>

      <div className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              은행명
            </label>
            <select
              value={formData.bank_name}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ ...formData, bank_name: value });
                if (value === '기타') {
                  setShowCustomBank(true);
                } else {
                  setShowCustomBank(false);
                  setCustomBankName('');
                }
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            >
              <option value="">은행을 선택하세요</option>
              <option value="KB국민은행">KB국민은행</option>
              <option value="신한은행">신한은행</option>
              <option value="하나은행">하나은행</option>
              <option value="우리은행">우리은행</option>
              <option value="SC제일은행">SC제일은행</option>
              <option value="한국씨티은행">한국씨티은행</option>
              <option value="카카오뱅크">카카오뱅크</option>
              <option value="토스뱅크">토스뱅크</option>
              <option value="새마을금고">새마을금고</option>
              <option value="신협">신협</option>
              <option value="우체국">우체국</option>
              <option value="기업은행">기업은행</option>
              <option value="농협은행">농협은행</option>
              <option value="수협은행">수협은행</option>
              <option value="기타">기타 (직접입력)</option>
            </select>
            
            {showCustomBank && (
              <input
                type="text"
                value={customBankName}
                onChange={(e) => setCustomBankName(e.target.value)}
                placeholder="은행명을 입력하세요"
                className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              계좌번호
            </label>
            <input
              type="text"
              value={formData.bank_account}
              onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
              placeholder="1234-5678-9012"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              예금주명
            </label>
            <input
              type="text"
              value={formData.account_holder}
              onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
              placeholder="홍길동"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>주의사항</strong><br />
              • 정확한 계좌 정보를 입력해주세요.<br />
              • 잘못된 계좌 정보로 인한 손실은 책임지지 않습니다.<br />
              • 계좌 정보는 인출 시에만 사용됩니다.
            </p>
          </div>

          <button
            type="submit"
            disabled={updateAccountMutation.isLoading}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 disabled:opacity-50"
          >
            {updateAccountMutation.isLoading ? '저장 중...' : '계좌 정보 저장'}
          </button>
        </form>
      </div>
    </div>
  );
};

// VIP 공지사항 컴포넌트
const VipNotices = () => {
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  // 유튜브 스타일 삽입
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* 일반 유튜브 영상 (16:9) */
      .vip-youtube-video-container {
        position: relative;
        width: 100%;
        max-width: 800px;
        margin: 2rem auto;
        padding-bottom: 56.25%; /* 16:9 비율 */
        height: 0;
        overflow: hidden;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border-radius: 1.5rem;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      }
      
      .vip-youtube-video-container iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 0;
        border-radius: 1.5rem;
      }
      
      /* 유튜브 쇼츠 (9:16) */
      .vip-youtube-shorts-container {
        position: relative;
        width: 100%;
        max-width: 450px;
        margin: 2rem auto;
        padding-bottom: 177.78%; /* 9:16 비율 */
        height: 0;
        overflow: hidden;
        background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        border-radius: 1.5rem;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      }
      
      .vip-youtube-shorts-container iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 0;
        border-radius: 1.5rem;
      }
      
      /* 모바일에서 쇼츠 크기 조정 */
      @media (max-width: 640px) {
        .vip-youtube-shorts-container {
          max-width: 300px;
        }
      }

      /* VIP 이미지 스타일 */
      .vip-notice-content img {
        max-width: 100%;
        height: auto;
        margin: 2rem auto;
        display: block;
        border-radius: 1.5rem;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      }

      /* VIP 마크다운 스타일 */
      .vip-notice-content h1,
      .vip-notice-content h2,
      .vip-notice-content h3,
      .vip-notice-content h4,
      .vip-notice-content h5,
      .vip-notice-content h6 {
        margin-top: 2rem;
        margin-bottom: 1rem;
        font-weight: 700;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      .vip-notice-content p {
        margin-bottom: 1rem;
        line-height: 1.75;
        color: #4b5563;
      }

      .vip-notice-content ul,
      .vip-notice-content ol {
        margin-left: 2rem;
        margin-bottom: 1rem;
        color: #4b5563;
      }

      .vip-notice-content blockquote {
        border-left: 4px solid;
        border-image: linear-gradient(135deg, #667eea 0%, #764ba2 100%) 1;
        padding-left: 1rem;
        margin: 1.5rem 0;
        color: #6b7280;
        font-style: italic;
      }

      .vip-notice-content code {
        background: linear-gradient(135deg, #f3e7fc 0%, #fce7f3 100%);
        padding: 0.125rem 0.375rem;
        border-radius: 0.375rem;
        color: #764ba2;
        font-size: 0.875rem;
      }

      .vip-notice-content pre {
        background: linear-gradient(135deg, #1e1b4b 0%, #581c87 100%);
        padding: 1rem;
        border-radius: 1rem;
        overflow-x: auto;
        margin: 1.5rem 0;
      }

      .vip-notice-content pre code {
        background: transparent;
        color: #f3e7fc;
        padding: 0;
      }

      .vip-notice-content table {
        width: 100%;
        border-collapse: collapse;
        margin: 1.5rem 0;
      }

      .vip-notice-content th {
        background: linear-gradient(135deg, #f3e7fc 0%, #fce7f3 100%);
        padding: 0.75rem;
        text-align: left;
        font-weight: 600;
        color: #581c87;
      }

      .vip-notice-content td {
        padding: 0.75rem;
        border-bottom: 1px solid #e5e7eb;
      }

      .vip-notice-content a {
        color: #7c3aed;
        text-decoration: underline;
        transition: color 0.2s;
      }

      .vip-notice-content a:hover {
        color: #5b21b6;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const { data: noticeData, isLoading, refetch } = useQuery(
    ['vip-notices', page, searchQuery],
    () => api.get(`/notices?page=${page}&limit=10${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`).then(res => res.data)
  );

  const handleNoticeClick = async (notice) => {
    try {
      const response = await api.get(`/notices/${notice.id}`);
      setSelectedNotice(response.data);
      
      // 조회수 증가
      await api.patch(`/notices/${notice.id}/view`);
    } catch (error) {
      console.error('공지사항 상세 정보 조회 실패:', error);
      setSelectedNotice(notice);
    }
  };

  const formatDate = (date) => {
    return format(new Date(date), 'yyyy.MM.dd HH:mm', { locale: ko });
  };

  const getTargetAudienceLabel = (target) => {
    if (!target) return '전체';
    
    // 개별 공지인 경우
    if (target === 'individual') {
      return '나만의 공지';
    }
    
    const targets = target.split(',');
    const labels = targets.map(t => {
      switch (t.trim()) {
        case 'all': return '전체';
        case 'contractor': return '소장';
        case 'customer': return '고객';
        case 'vip_customer': return 'VIP';
        default: return t.trim();
      }
    });
    
    return labels.join(', ');
  };

  const getTargetAudienceColor = (target) => {
    if (!target) return 'from-gray-400 to-gray-600';
    
    // 개별 공지인 경우 특별한 그라데이션
    if (target === 'individual') {
      return 'from-purple-600 via-pink-500 to-rose-500';
    }
    
    const targets = target.split(',');
    
    if (targets.includes('all')) {
      return 'from-blue-400 to-indigo-600';
    } else if (targets.includes('vip_customer')) {
      return 'from-yellow-400 to-orange-600';
    } else if (targets.length > 1) {
      return 'from-purple-400 to-pink-600';
    } else {
      switch (targets[0].trim()) {
        case 'contractor': return 'from-indigo-400 to-purple-600';
        case 'customer': return 'from-green-400 to-emerald-600';
        case 'vip_customer': return 'from-yellow-400 to-orange-600';
        default: return 'from-gray-400 to-gray-600';
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-purple-200 rounded-full animate-pulse"></div>
            <div className="absolute top-0 left-0 w-20 h-20 border-4 border-purple-600 rounded-full animate-spin border-t-transparent"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">공지사항을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full">
      {/* 헤더 */}
      <div className="mb-4 sm:mb-6 lg:mb-8">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-indigo-500/20 rounded-xl sm:rounded-2xl lg:rounded-3xl blur-xl"></div>
          <div className="relative bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl lg:rounded-3xl p-3 sm:p-4 lg:p-6 border border-purple-100 shadow-xl">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
                <div className="p-1.5 sm:p-2 lg:p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg flex-shrink-0">
                  <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5 lg:h-7 lg:w-7 text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-base sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent truncate">
                    VIP 공지사항
                  </h1>
                  <p className="text-xs lg:text-sm text-gray-600 mt-0.5">VIP 회원님을 위한 특별 소식</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {!showSearch ? (
                  <>
                    <button
                      onClick={() => setShowSearch(true)}
                      className="p-1.5 sm:p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 shadow-lg"
                      title="검색"
                    >
                      <MagnifyingGlassIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    </button>
                    <div className="px-2 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-purple-50 to-pink-50 rounded-md sm:rounded-lg border border-purple-200">
                      <span className="text-xs lg:text-sm font-bold text-purple-700 whitespace-nowrap">
                        총 {noticeData?.total || 0}개
                      </span>
                    </div>
                    <div className="px-2 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-md sm:rounded-lg border border-yellow-200">
                      <span className="text-xs lg:text-sm font-bold text-orange-700 whitespace-nowrap">
                        VIP 전용
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            setPage(1);
                            refetch();
                          }
                        }}
                        placeholder="VIP 공지 검색..."
                        className="w-36 sm:w-56 px-3 py-1.5 pr-8 bg-gradient-to-r from-purple-50/50 to-pink-50/50 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm backdrop-blur-sm"
                        autoFocus
                      />
                      {searchQuery && (
                        <button
                          onClick={() => {
                            setSearchQuery('');
                            setPage(1);
                            refetch();
                          }}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-purple-400 hover:text-purple-600"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setShowSearch(false);
                        setSearchQuery('');
                        setPage(1);
                        refetch();
                      }}
                      className="p-1.5 bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg hover:from-gray-200 hover:to-gray-300 transition-colors"
                      title="닫기"
                    >
                      <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 검색 결과 표시 */}
      {searchQuery && (
        <div className="mb-4 flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-3 border border-purple-200">
          <p className="text-sm text-purple-700">
            <span className="font-bold">"{searchQuery}"</span> 검색 결과
            <span className="font-bold text-purple-900 ml-1">{noticeData?.total || 0}개</span>
          </p>
          <button
            onClick={() => {
              setSearchQuery('');
              setPage(1);
              refetch();
            }}
            className="text-sm text-purple-600 hover:text-purple-700 font-bold"
          >
            전체 보기
          </button>
        </div>
      )}

      {/* 공지사항 목록 */}
      <div className="space-y-2 sm:space-y-3 lg:space-y-4 w-full">
        {noticeData?.notices?.map((notice, index) => (
          <div
            key={notice.id}
            onClick={() => handleNoticeClick(notice)}
            className="group relative cursor-pointer transition-all duration-300 hover:scale-[1.01] active:scale-[0.99]"
          >
            {/* 호버 그라데이션 효과 */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-400/10 via-pink-400/10 to-indigo-400/10 rounded-lg sm:rounded-xl lg:rounded-2xl opacity-0 group-hover:opacity-100 blur transition-all duration-300"></div>
            
            {/* 카드 본체 */}
            <div className="relative bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 group-hover:border-purple-200 overflow-hidden transition-all duration-300">
              {/* 상단 그라데이션 라인 */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              
              <div className="p-4 sm:p-5 lg:p-6">
                {/* 상단 정보 */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {notice.is_important ? (
                      <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-red-500 to-rose-500 text-white text-xs font-bold rounded-full shadow-sm">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                        </span>
                        <span>중요</span>
                      </div>
                    ) : null}
                    <div className={`inline-flex items-center px-2.5 py-1 bg-gradient-to-r ${getTargetAudienceColor(notice.target_audience)} text-white text-xs font-bold rounded-full shadow-sm ${
                      notice.target_audience === 'individual' ? 'animate-pulse' : ''
                    }`}>
                      {notice.target_audience === 'individual' ? '🔐 ' : ''}{getTargetAudienceLabel(notice.target_audience)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-gray-400 text-xs">
                    {notice.view_count > 0 && (
                      <>
                        <div className="flex items-center gap-1">
                          <EyeIcon className="h-3.5 w-3.5" />
                          <span>{notice.view_count}</span>
                        </div>
                        <div className="w-px h-3 bg-gray-300"></div>
                      </>
                    )}
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">{formatDate(notice.created_at)}</span>
                      <span className="sm:hidden">{format(new Date(notice.created_at), 'MM.dd', { locale: ko })}</span>
                    </div>
                  </div>
                </div>

                {/* 제목 */}
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600 group-hover:bg-clip-text transition-all duration-300">
                  {notice.title}
                </h3>

                {/* 내용 미리보기 */}
                <p className="text-sm text-gray-600 line-clamp-2 mb-3 leading-relaxed">
                  {notice.content
                    ?.replace(/<[^>]*>/g, '') // HTML 태그 제거
                    .replace(/!\[.*?\]\(.*?\)/g, '') // 이미지 마크다운 제거
                    .replace(/\[.*?\]\(.*?\)/g, '') // 링크 마크다운 제거
                    .replace(/[#*`\[\]>]/g, '') // 마크다운 문법 제거
                    .replace(/\n+/g, ' ') // 줄바꿈 공백으로
                    .replace(/\s+/g, ' ') // 여러 공백 하나로
                    .trim()
                    .substring(0, 100) || '내용 없음'}...
                </p>

                {/* 하단 액션 */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    {notice.files && notice.files.length > 0 && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-lg">
                        <PaperClipIcon className="h-3.5 w-3.5 text-gray-500" />
                        <span className="text-xs text-gray-600">{notice.files.length}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1 px-2 py-1 bg-purple-50 rounded-lg">
                      <UserIcon className="h-3.5 w-3.5 text-purple-500" />
                      <span className="text-xs text-purple-600 font-medium">{notice.author || '관리자'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-purple-500 group-hover:text-pink-500 transition-colors">
                    <span className="text-xs font-medium">자세히</span>
                    <ChevronRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 더보기 버튼 */}
      {noticeData?.total > page * 10 && (
        <div className="mt-6 lg:mt-8 text-center">
          <button
            onClick={() => setPage(page + 1)}
            className="inline-flex items-center gap-2 px-6 lg:px-8 py-2.5 lg:py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl lg:rounded-2xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <span className="text-sm lg:text-base">더 많은 공지사항 보기</span>
            <ArrowDownTrayIcon className="h-4 w-4 lg:h-5 lg:w-5" />
          </button>
        </div>
      )}

      {/* 공지사항 없을 때 */}
      {(!noticeData?.notices || noticeData.notices.length === 0) && !isLoading && (
        <div className="mt-8">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-400/10 via-pink-400/10 to-indigo-400/10 rounded-2xl lg:rounded-3xl blur-xl"></div>
            <div className="relative bg-white/95 backdrop-blur-sm rounded-2xl lg:rounded-3xl p-8 lg:p-12 text-center border border-purple-100 shadow-xl">
              <div className="w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {searchQuery ? (
                  <MagnifyingGlassIcon className="h-10 w-10 lg:h-12 lg:w-12 text-purple-500" />
                ) : (
                  <SparklesIcon className="h-10 w-10 lg:h-12 lg:w-12 text-purple-500" />
                )}
              </div>
              <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-2">
                {searchQuery ? '검색 결과가 없습니다' : '공지사항이 없습니다'}
              </h3>
              <p className="text-sm lg:text-base text-gray-600">
                {searchQuery ? '다른 검색어를 입력해보세요' : 'VIP 회원님을 위한 새로운 소식이 곧 업데이트 될 예정입니다'}
              </p>
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setPage(1);
                    refetch();
                  }}
                  className="mt-4 inline-flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
                >
                  전체 공지사항 보기
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 공지사항 상세 모달 */}
      {selectedNotice && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="relative max-w-4xl w-full h-[90vh] max-h-[90vh] flex flex-col">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-pink-600 to-indigo-600 rounded-2xl lg:rounded-3xl blur-sm opacity-20"></div>
            <div className="relative bg-white/95 backdrop-blur-xl rounded-2xl lg:rounded-3xl shadow-2xl border border-white/50 overflow-hidden flex flex-col h-full">
              {/* 모달 헤더 */}
              <div className="flex-shrink-0 bg-gradient-to-r from-purple-600 to-pink-600 p-4 sm:p-6 text-white">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {selectedNotice.is_important === true && (
                        <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/20 backdrop-blur text-white text-xs font-bold rounded-full">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                          </span>
                          <span>중요</span>
                        </div>
                      )}
                      <div className="px-2.5 py-1 bg-white/20 backdrop-blur text-white text-xs font-bold rounded-full">
                        {getTargetAudienceLabel(selectedNotice.target_audience)}
                      </div>
                    </div>
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2 break-all pr-2">{selectedNotice.title}</h2>
                    <div className="flex flex-wrap items-center gap-3 text-white/80 text-xs sm:text-sm">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="h-3.5 w-3.5" />
                        <span>{formatDate(selectedNotice.created_at)}</span>
                      </div>
                      {selectedNotice.view_count > 0 && (
                        <div className="flex items-center gap-1">
                          <EyeIcon className="h-3.5 w-3.5" />
                          <span>{selectedNotice.view_count}회</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <UserIcon className="h-3.5 w-3.5" />
                        <span>{selectedNotice.author || '관리자'}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedNotice(null)}
                    className="flex-shrink-0 p-1.5 sm:p-2 hover:bg-white/20 rounded-lg sm:rounded-xl transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                </div>
              </div>

              {/* 모달 내용 */}
              <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto overflow-x-hidden vip-notice-content break-all">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    img: ({node, ...props}) => (
                      <img {...props} className="max-w-full h-auto mx-auto rounded-2xl shadow-xl" />
                    ),
                    iframe: ({node, ...props}) => {
                      const src = props.src || '';
                      const isShorts = src.includes('shorts');
                      const containerClass = isShorts ? 'vip-youtube-shorts-container' : 'vip-youtube-video-container';
                      
                      return (
                        <div className={containerClass}>
                          <iframe {...props} />
                        </div>
                      );
                    }
                  }}
                >
                  {selectedNotice.content}
                </ReactMarkdown>

                {/* 첨부파일 */}
                {selectedNotice.files && selectedNotice.files.length > 0 && (
                  <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <PaperClipIcon className="h-5 w-5 text-purple-600" />
                      첨부파일
                    </h3>
                    <div className="space-y-2">
                      {selectedNotice.files.map((file) => (
                        <a
                          key={file.id}
                          href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${file.file_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-white rounded-xl hover:bg-purple-100 transition-colors group"
                        >
                          {file.file_path.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                            <PhotoIcon className="h-5 w-5 text-purple-600 group-hover:scale-110 transition-transform" />
                          ) : (
                            <DocumentIcon className="h-5 w-5 text-purple-600 group-hover:scale-110 transition-transform" />
                          )}
                          <span className="text-sm text-gray-700 group-hover:text-purple-700 font-medium">
                            {file.original_name}
                          </span>
                          <span className="text-xs text-gray-500 ml-auto">
                            {(file.file_size / 1024).toFixed(1)} KB
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 모달 푸터 */}
              <div className="flex-shrink-0 p-4 sm:p-6 border-t border-gray-200 bg-white">
                <button
                  onClick={() => setSelectedNotice(null)}
                  className="w-full py-2.5 sm:py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl sm:rounded-2xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VipDashboard;